import { CacheProvider } from '@emotion/react';
import { CssBaseline } from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { Outlet, useLocation, useNavigate, useNavigation, useParams } from "react-router-dom";
import { emotionCache } from '../../utils/emotionCache';
import { getNavIcon, matchPathInMap } from '../../utils/getNavIcon';
import { theme } from "../../utils/theme";
import { AlertComp } from '../AlertComp';
import { AppBarComp } from "../AppBarComp";
import { useGlobal } from '../GlobalProviderLayout';
import { CLOSE_TOAST, IToast, OPEN_TOAST, SET_PREV_LOCATION } from '../GlobalProviderLayout/GlobalProviderLayoutModel';
import { Loading } from "../Loading";
import { NavDrawer } from "../NavDrawer";
import { AuthenticationMethod, STRING_BOOLEAN, subdestinationMap } from './RootLayoutModels';

/**
 * RootLayout is a Layout Route (see react-router-dom) and is responsible for 
 * 1. Handling auth events and state
 * 2. Rendering common UI elements including: AppBarComp, NavDrawer, and Loading
 * 3. Providing MUI contexts (Theme for MUI and CacheProvider for Emotion)
 * @returns 
 */
const RootLayout = () => {
  const auth = useAuth();
  const global = useGlobal();
  const location = useLocation();
  const navigate = useNavigate();
  const navigation = useNavigation();
  const params = useParams();
  const { state, dispatch } = useGlobal();
  const [navDrawerOpen, setNavDrawerOpen] = useState<boolean>(false);
  const navigationInProg = navigation.state === "loading" || navigation.state === "submitting";
  const previousLocation = useRef<string | undefined>(undefined);
  const lastLoginTime = auth.isAuthenticated ? auth?.user?.profile.lastLoginTime as string || '' : '';
  const isTelephoneBoard = auth.user?.profile.isTelephoneBoard === STRING_BOOLEAN.true;
  const isLinesComplementBoard = auth.user?.profile.isLinesComplementBoard === STRING_BOOLEAN.true;
  const authMethod = auth.user?.profile.authenticationMethod as AuthenticationMethod;
  const isIVR = authMethod === AuthenticationMethod.ivr;
  const hasVerifiedEmail = String(auth.user?.profile.hasVerifiedEmail) === STRING_BOOLEAN.true;
  const signedIn = auth.isAuthenticated;
  const navIcon = getNavIcon(location);

  const signIn = async () => {
    try {
      await auth.signinRedirect({ state: location, extraQueryParams: { timestamp: Date.now() + (10 * 60 * 1000) } });
    } catch (e: any) {
      alert("Error redirecting to sign in page. Please try again");
    }
  }

  const signOut = async () => {
    try {
      await auth.signoutRedirect();
    } catch (e: any) {
      alert("Error signing out. Please try again");
    }
  }

  const handleBellNotification = () => {
    const payload: IToast = {
      msg: "Please update your email in your profile.",
      closeTime: 5000,
      severity: "info",
    };
    global.dispatch({ type: OPEN_TOAST, payload });
  }

  /**
   * Navigate to location provided. If location is same as current location, ignore navigation
   * Accoring to react-router's GitHub issues page:
   * No officially supported way to refresh/reload a Route without refreshing the page using browser
   * [Link](https://github.com/remix-run/react-router/issues/7416)
   * @param to 
   * @returns 
   */
  const handleNavigation = async (to: Location | string) => {
    dispatch({ type: CLOSE_TOAST });
    if (to !== location.pathname) {
      previousLocation.current = location.pathname;
      navigate(to);
    }
  }

  /** 
   * Test Cases
   * * Navigate directly (no history)
   * * Navigate from page (history)
   */
  const appBarCompHandleBack = (level: number = 1) => {
    dispatch({ type: CLOSE_TOAST });
    if (previousLocation.current !== undefined) {
      navigate(-level);
    } else {
      /**
       * Need to change the behaviour of this for levels >= 2
       * This impl. can only retrieve params from the current url,
       * so context of params from historic dest. routes are lost
       * (assuming param names differ from the sub dest.)
       */
      let to = location.pathname;
      for (level; level > 0; level--) {
        const subdest = matchPathInMap(subdestinationMap, to);
        if (subdest) to = subdestinationMap[subdest];
      }
      const parameters = to.match(/:[A-Za-z]+/g) || [];
      for (let p of parameters) {
        // replace URL params w/ actual values
        to = to.replace(p, params[p.replace(':', '')] ?? '');
      }
      if (to === location.pathname) to = "/";
      navigate(to, { replace: true })
    }
  }

  useEffect(() => {
    /**
     * react-oidc-context/oidc-client-ts event
     * When silentRenew fails, retry once more in 5 seconds using signInSilent.
     * Note: signInSilent and silentRenew use refresh_token if its available. Otherwise, it will use attempt an
     * authorize with prompt=none in the background. An error in either of these renewal methods will cause this event
     */
    const sreCleanupFunc = auth.events.addSilentRenewError(err => {
      console.log("Handling silentRenewError", err);
      setTimeout(auth.signinSilent, 5000);
    });

    /**
     * react-oidc-context/oidc-client-ts event
     * When the access token is expiring, try to acquire the mutual exclusive lock and trigger the renew process.
     * This process is handled mannualy because we want to add a mutex lock to prevent the tabs from concurrently
     * performing the refresh process. This will avoid the token refresh race conditions across tabs.
     * Note: the default time to fresh tokens is 60 seconds before the access token expiry
     */
    const ateCleanupFunc = auth.events.addAccessTokenExpiring(async () => {
      console.log('Handling accessTokenExpiring');

      await navigator.locks.request("refresh_lock", async () => {
        // add some delays in between refresh token requests
        await new Promise(res => setTimeout(res, 200));
        console.log('Trying to renew tokens at ', new Date().getTime());
        await auth.signinSilent();
      });
    })

    /**
     * react-oidc-context/oidc-client-ts event
     * When access_token has expired, attempt to signInSilent. If that fails, signoutRedirect
     * Note: This can occur if the access_token has expired while using the app or if the app is started 
     * and the stored access_token is expired
     */
    const atedCleanupFunc = auth.events.addAccessTokenExpired(async () => {
      console.log("Handling accessTokenExpired");
      console.log("Trying to sign in silently");

      await navigator.locks.request("refresh_lock", async () => {
        console.log('Trying to renew tokens at ', new Date().getTime());
        const user = await auth.signinSilent();
        // the following code will run if the user does NOT have a valid refresh token
        // i.e. revisit the site after refresh token expires or past the authentication cookie lifetime
        // signinSilent doesn't throw an exception even if it receives a 400 or 500 response
        // we check the user object to determine whether the sign in process has succeeded or not
        if (user == null) {
          console.log("Silent sign in failed");
          console.log("Removing user");
          // removing the user as he has already signed out
          auth.removeUser();
        } else {
          // the following code will run if the user has a valid refresh token
          // i.e. revisit the site before refresh token expires and within the authentication cookie lifetime
          // re-navgiate to the same route forcing another request to the API server
          // with the updated tokens
          console.log("Silent sign in succeeded");
          navigate(0);
        }
      });
    });

    /**
     * react-oidc-context/oidc-client-ts event
     * When user's has signed out sign out of all tabs
     * Note: This handler also ensures that when a browser is closed, re-opened, navigated to the app, 
     * that the user is signed out because they no longer have a session with OP (closing the browser
     * would have destroyed the session cookie)
     */
    const usoCleanupFunc = auth.events.addUserSignedOut(() => {
      console.log("Handling userSignedOut");
      auth.removeUser();
      navigate(0);
    });

    /** Clean up all event handlers */
    return () => {
      sreCleanupFunc();
      ateCleanupFunc();
      atedCleanupFunc();
      usoCleanupFunc();
    }
  }, [auth, navigate]);

  useEffect(() => {
    if (state.previousLocation !== location.pathname) {
      dispatch({ type: SET_PREV_LOCATION, payload: location.pathname });
    }
  }, [location, dispatch, state.previousLocation]);

  return <div>
    <CacheProvider value={emotionCache}>
      {auth.isLoading && !signedIn ? <Loading /> : <>
        <CssBaseline />
        <ThemeProvider theme={theme}>
          <AlertComp {...state.toast} onClose={() => dispatch({ type: CLOSE_TOAST })} />
          <NavDrawer
            board={auth.user?.profile.board as string}
            isIVR={isIVR}
            isTelephone={isTelephoneBoard}
            open={navDrawerOpen}
            signedIn={signedIn}
            nav={navigation.location?.pathname || location.pathname}
            onNavTo={handleNavigation}
            onClose={() => { setNavDrawerOpen(false); }}
            onSignOut={signOut}
            lastLoginTime={lastLoginTime}
            isDOAAdmin={signedIn && auth.user?.profile.isDoaAdmin === "True"}
            isLinesComplementBoard={isLinesComplementBoard}
          />
          <AppBarComp
            isIVR={isIVR}
            hasVerifiedEmail={hasVerifiedEmail}
            navIcon={navIcon}
            navIconLabel=''
            avatar={auth.isAuthenticated ?
              ((auth?.user?.profile.initials as unknown as string) === "" ? "X" : auth?.user?.profile.initials as unknown as string)
              : undefined}
            onTapMenu={() => { setNavDrawerOpen(true); }}
            onTapBack={appBarCompHandleBack}
            onTapAvatar={() => { handleNavigation("/my-profile"); }}
            onTapBell={handleBellNotification}
            onTapSignIn={signIn} />
          <div>
            {navigationInProg ? <Loading /> : <Outlet />}
          </div>
        </ThemeProvider>
      </>}
    </CacheProvider>
  </div>;
}

export default RootLayout;